import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useIsAccountAccessLoaded} from '../banking/useIsAccountAccessLoaded';
import {useCurrentCompany} from '../company/useCurrentCompany';
import {useIsEmployeeLoaded} from '../company/useIsEmployeeLoaded';
import {useIsAdmin} from '../access/useIsAdmin';
import {useIsAuthenticated} from '../access/useIsAuthenticated';
import {useIsFeaturesLoaded} from './useIsFeaturesLoaded';
import {useIsPsd2ObAvailable} from './useIsPsd2ObAvailable';

// Longest the route gate may hold its loader before giving up and calling the path a 404.
const ROUTE_ACCESS_TIMEOUT_MS = 10000;

/**
 * Whether everything the authenticated route table is built from has arrived, so a path that is
 * not registered can be called a 404 rather than "not registered yet".
 *
 * Routes are registered from company feature flags, which read false until `company.features`
 * loads, and from the user's role. Until then a direct link to an available page falls through to
 * the catch-all — a 404 frame followed by the page.
 *
 * A user with no company (no companies, or only disabled ones) never gets features requested at
 * all, so once the company list has settled and holds no enabled company this resolves true;
 * waiting on features would spin forever for them. While an enabled company exists but is not
 * selected yet, it keeps waiting — the selection is dispatched just after the list lands.
 *
 * Always ends: a FAILED company-features request settles the gate (`features.settled`) with every
 * gated module unavailable, so the path is a 404, and a request that never settles at all is cut
 * off by ROUTE_ACCESS_TIMEOUT_MS. Late data still wins — the route registers and replaces the 404.
 * ⚠ A failed employee-profile request is NOT covered here: it holds the whole app on its
 * full-page spinner, above the routes.
 *
 * After a company switch the employee is refetched but not cleared, so the gate also waits for an
 * employee that belongs to the current company — otherwise it resolves against the previous
 * company's role.
 *
 * Non-admin access to transactions comes from Open Banking account access, so that is awaited
 * only for them and only when Open Banking is available.
 *
 * @returns {boolean} True once an unregistered path is definitively unavailable.
 *
 * @example
 * const isResolved = useIsRouteAccessResolved();
 * // render a loader instead of the 404 until isResolved
 */
export const useIsRouteAccessResolved = () => {
  const isEmployeeLoaded = useIsEmployeeLoaded();
  const employeeCompanyId = useSelector(state => state.user.employee?.company_id);
  const isCompaniesLoaded = useSelector(state => state.user.companiesLoaded);
  const hasEnabledCompany = useSelector(state => state.user.companies.some(company => company.account_enabled));
  const companyId = useCurrentCompany().id;
  const isFeaturesLoaded = useIsFeaturesLoaded();
  const isFeaturesSettled = useSelector(state => state.company?.features?.settled === true);
  const isPsd2ObAvailable = useIsPsd2ObAvailable();
  const isAdmin = useIsAdmin();
  const isAuthenticated = useIsAuthenticated();
  const isAccountAccessLoaded = useIsAccountAccessLoaded();

  // Keyed by company so a timeout on one company does not pre-expire the gate after a switch.
  const timeoutKey = companyId ?? '';
  const [timedOutKey, setTimedOutKey] = useState(null);

  const isResolvedByData = (() => {
    if (!isEmployeeLoaded || !isCompaniesLoaded) return false;
    if (!companyId) return !hasEnabledCompany;
    if (employeeCompanyId !== companyId) return false;
    if (isFeaturesSettled && !isFeaturesLoaded) return true;
    if (!isFeaturesLoaded) return false;

    return isAdmin || !isPsd2ObAvailable || isAccountAccessLoaded;
  })();

  useEffect(() => {
    // Not armed while signed out, and forgotten once the gate resolves: a timeout that fired on
    // the sign-in screen would otherwise resolve the gate the moment the user signs in.
    if (!isAuthenticated || isResolvedByData) {
      setTimedOutKey(null);
      return undefined;
    }
    const timer = setTimeout(() => setTimedOutKey(timeoutKey), ROUTE_ACCESS_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isAuthenticated, isResolvedByData, timeoutKey]);

  return isResolvedByData || timedOutKey === timeoutKey;
};
